import {
  NodoDto,
  RegistroDto,
  UpdateRegistroDto,
  useBancoFlussaggioCalcolaPortata,
  useGetRegistriDispositivo,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import NumberInput from "@/elements/Input/NumberInput";
import { SmallLoading } from "@/components/Loading";
import {
  Alert,
  Card,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { isNil, keyBy } from "lodash";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

export function isBancoDiFlussaggioPortataInRange(
  registri: RegistroDto[],
): boolean {
  const portPid = getBancoDiFlussaggioPortata(registri);
  return portPid !== null && portPid >= 10 && portPid <= 1000;
}

function getBancoDiFlussaggioPortata(registri: RegistroDto[]) {
  const portPidRaw = registri.find((r) => r.registro === "portpid")?.value;
  return toNumeric(portPidRaw);
}

function toNumeric(value: unknown) {
  if (value == null) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export type Registri_BANCO_DI_FLUSSAGGIO = Record<string, any>;

type NumberFieldConfig = {
  name: string;
  label: string;
  min?: number;
  max?: number;
  decimals?: number;
  unit?: string;
};

type BoolFieldConfig = {
  name: string;
  label: string;
};

type TextFieldConfig = {
  name: string;
  label: string;
};

const numericFields: NumberFieldConfig[] = [
  {
    name: "allaltaT",
    label: "Allarme alta temperatura flussaggio",
    min: 0,
    max: 200,
  },
  {
    name: "allTmaxricir",
    label: "Max tempo ricircolo",
    min: 0,
    max: 999,
    decimals: 1,
    unit: "s",
  },
  {
    name: "allDP2-4",
    label: "Valore allarme pressione delta 2-4",
    min: 0,
    max: 30,
  },
  {
    name: "allPminTF",
    label: "Allarme pressione minima filtro tempo",
    min: 0,
    max: 999,
    unit: "s",
  },
  { name: "allDP3-4", label: "Valore allarme pres 3-4", min: 0, max: 30 },
  {
    name: "velmanflus1",
    label: "Velocità manuale flussaggio 1",
    min: 0,
    max: 100,
  },
  {
    name: "allPmaxF",
    label: "Pressione max flussaggio",
    min: 0,
    max: 30,
    unit: "bar",
  },
  {
    name: "allPminF",
    label: "Pressione min flussaggio",
    min: 0,
    max: 30,
    unit: "bar",
  },
  { name: "viscosp", label: "Viscosità set point portata", min: 0, max: 100 },
  {
    name: "wpresvelocita",
    label: "Warning pressione riduzione velocità",
    decimals: 2,
  },
  {
    name: "tstartciclo",
    label: "Temperatura start ciclo",
    decimals: 2,
    unit: "°C",
  },
  {
    name: "tempricir",
    label: "Temperatura ricircolo",
    min: 0,
    max: 200,
    unit: "°C",
  },
  {
    name: "velpricirc",
    label: "Velocità pompa ricircolo",
    min: 0,
    max: 1500,
  },
  {
    name: "allTempminricir",
    label: "Temperatura minima pompa ricircolo",
    min: 0,
    max: 100,
    unit: "°C",
  },
  {
    name: "allTempmaxricir",
    label: "Temperatura massima pompa ricircolo",
    min: 0,
    max: 200,
    unit: "°C",
  },
  {
    name: "velmanflus2",
    label: "Velocità manuale flussaggio 2",
    min: 0,
    max: 100,
  },
  { name: "ncurve", label: "Variables numero curve", decimals: 2 },
  { name: "coeffcurvk", label: "Variables coefficiente curva k", decimals: 3 },
  { name: "nreynolds", label: "Variables numero reynolds", decimals: 3 },
  {
    name: "sptemp",
    label: "Variables SP temperatura portata",
    decimals: 2,
    unit: "°C",
  },
  { name: "spcont4", label: "SP contaminazione 4", min: 0, max: 30 },
  { name: "spcont6", label: "SP contaminazione 6", min: 0, max: 30 },
  { name: "spcont14", label: "SP contaminazione 14", min: 0, max: 30 },
  { name: "spcont21", label: "SP contaminazione 21", min: 0, max: 30 },
  { name: "lunghtubo", label: "Lunghezza tubo", decimals: 2 },
  { name: "diamtubo", label: "Diametro del tubo", decimals: 2, unit: "mm" },
  { name: "allflusmax", label: "Allarme flusso massimo", decimals: 2 },
  { name: "allflusmin", label: "Allarme flusso minimi", decimals: 2 },
  {
    name: "allTflusmax",
    label: "Tempo controllo allarme flusso massimo",
    min: 0,
    decimals: 2,
    unit: "s",
  },
  {
    name: "allTflusmin",
    label: "Tempo controllo allarme flusso minimo",
    min: 0,
    decimals: 2,
    unit: "s",
  },
];

const boolFields: BoolFieldConfig[] = [
  { name: "selmanauto", label: "Funzionamento automatico" },
  { name: "fineconcont", label: "Fine ciclo con contaminazione" },
  { name: "bloccomacc", label: "Blocco macchina" },
  { name: "attricircolo", label: "Attivazione ricircolo" },
  { name: "attfluss", label: "Attivazione flussaggio" },
  { name: "abflus1", label: "Includi flussaggio 1" },
  { name: "abflus2", label: "Includi flussaggio 2" },
  { name: "attresgr1", label: "Attivazione resistenze gruppo 1" },
  { name: "attresgr2", label: "Attivazione resistenze gruppo 2" },
  { name: "attresgr3", label: "Attivazione resistenze gruppo 3" },
  { name: "attresgr4", label: "Attivazione resistenze gruppo 4" },
  { name: "attresgr5", label: "Attivazione resistenze gruppo 5" },
  { name: "attresgr6", label: "Attivazione resistenze gruppo 6" },
];

const textFields: TextFieldConfig[] = [
  { name: "email1", label: "Email 1" },
  { name: "email2", label: "Email 2" },
];

const supportedFields = [
  ...numericFields.map((x) => x.name),
  ...boolFields.map((x) => x.name),
  ...textFields.map((x) => x.name),
] as const;

type RegistroFieldName = (typeof supportedFields)[number];

const numericFieldMap = Object.fromEntries(
  numericFields.map((field) => [field.name, field]),
) as Record<string, NumberFieldConfig>;

const boolFieldMap = Object.fromEntries(
  boolFields.map((field) => [field.name, field]),
) as Record<string, BoolFieldConfig>;

const textFieldMap = Object.fromEntries(
  textFields.map((field) => [field.name, field]),
) as Record<string, TextFieldConfig>;

const mappedFields: RegistroFieldName[] = [
  "selmanauto",
  "tempricir",
  "attricircolo",
  "attfluss",
  "attresgr1",
  "attresgr2",
  "attresgr3",
  "attresgr4",
  "attresgr5",
  "attresgr6",
  "velpricirc",
  "allaltaT",
  "allTmaxricir",
  "allDP2-4",
  "allDP3-4",
  "allPminF",
  "allPmaxF",
  "tstartciclo",
  "diamtubo",
  "lunghtubo",
  "sptemp",
  "viscosp",
  "nreynolds",
  "ncurve",
  "coeffcurvk",
  "email1",
  "email2",
  "allPminTF",
  "allTempminricir",
  "allTempmaxricir",
  "allflusmax",
  "allTflusmax",
  "wpresvelocita",
  "spcont4",
  "spcont6",
  "spcont14",
  "spcont21",
  "email1",
  "email2",
  "allTflusmin",
  "allflusmin",
];

const otherFields = supportedFields.filter(
  (field) => !mappedFields.includes(field),
) as RegistroFieldName[];

export function registriToForm_BANCO_DI_FLUSSAGGIO(
  _registri: RegistroDto[],
): Partial<Registri_BANCO_DI_FLUSSAGGIO> {
  const registri = keyBy(_registri, "registro");
  const result: Record<string, any> = {};

  for (const field of supportedFields) {
    const raw = registri[field]?.value;

    if (boolFields.some((x) => x.name === field)) {
      result[field] = raw === "1" || raw === "true";
      continue;
    }

    if (textFields.some((x) => x.name === field)) {
      result[field] = raw === "/" ? "" : raw || "";
      continue;
    }

    if (isNil(raw)) {
      result[field] = undefined;
      continue;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      result[field] = undefined;
      continue;
    }

    result[field] =
      field === "allTmaxricir" || field === "allPminTF"
        ? parsed / 1000
        : parsed;
  }

  return result;
}

function mapEmptyStringToSlash(value: string) {
  if (value === "") {
    return "/";
  }
  return value;
}

export function formToRegistri_BANCO_DI_FLUSSAGGIO(
  form: Registri_BANCO_DI_FLUSSAGGIO,
): UpdateRegistroDto[] {
  const topicPrefix = "var";

  return supportedFields
    .map((field) => {
      const value = form[field];
      if (isNil(value)) {
        return undefined;
      }

      if (boolFields.some((x) => x.name === field)) {
        return { registro: field, topicPrefix, value: value ? "1" : "0" };
      }

      if (textFields.some((x) => x.name === field)) {
        return {
          registro: field,
          topicPrefix,
          value: mapEmptyStringToSlash(`${value ?? ""}`),
        };
      }

      if (field === "allTmaxricir" || field === "allPminTF") {
        return {
          registro: field,
          topicPrefix,
          value: Number(value) * 1000,
        };
      }

      return {
        registro: field,
        topicPrefix,
        value,
      };
    })
    .filter(Boolean) as UpdateRegistroDto[];
}

export function EditorRegistri_BANCO_DI_FLUSSAGGIO({
  nodo,
  control,
  readOnly = false,
  dispositivoLastInfo,
  saving = false,
}: {
  nodo: NodoDto;
  control: Control<Registri_BANCO_DI_FLUSSAGGIO>;
  readOnly?: boolean;
  dispositivoLastInfo?: Record<string, any> | null;
  saving?: boolean;
}) {
  const { t } = useTranslation();
  const { mutateAsync: calcolaPortataAsync, isLoading: calcolaPortataLoading } =
    useBancoFlussaggioCalcolaPortata();
  const { data: registri, refetch } = useGetRegistriDispositivo(nodo.id);

  const viscositaRealeRaw = dispositivoLastInfo?.viscoreale;
  const viscositaReale =
    isNil(viscositaRealeRaw) || `${viscositaRealeRaw}`.trim() === ""
      ? ""
      : `${viscositaRealeRaw}`;

  const portPidValue = getBancoDiFlussaggioPortata(registri?.data ?? []);
  const portataInRange = isBancoDiFlussaggioPortataInRange(
    registri?.data ?? [],
  );

  async function handleCalcola() {
    try {
      const response = await calcolaPortataAsync({ id: nodo.id });
      refetch();
      const portataInRange = isBancoDiFlussaggioPortataInRange(
        response.data.map((x) => {
          return {
            registro: x.registro,
            time: x.newValueTime ?? "",
            value: x.newValue ?? "",
          };
        }),
      );
      if (portataInRange) {
        toast.success(
          t("dispositivoActions.toast.bancoFlussaggio.portataCalcolata"),
        );
      } else {
        toast.warn(
          t("dispositivoActions.toast.bancoFlussaggio.portataFuoriRange"),
        );
      }
    } catch {
      toast.error(t("dispositivoActions.toast.error"));
    }
  }

  const controlsLocked = readOnly || saving || calcolaPortataLoading;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SectionCard title="Generale macchina">
            <Grid item xs={12} md={6}>
              <Grid container gap={2}>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["selmanauto"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attricircolo"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attfluss"]}
                    readOnly={readOnly}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container gap={2}>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr1"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr2"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr3"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr4"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr5"]}
                    readOnly={readOnly}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BooleanField
                    control={control}
                    config={boolFieldMap["attresgr6"]}
                    readOnly={readOnly}
                  />
                </Grid>
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="SP flussaggio automatico">
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="tstartciclo"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="allflusmin"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="allTflusmin"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="allflusmax"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="allTflusmax"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="wpresvelocita"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Calcolo portata">
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="diamtubo"
                label="Diametro interno tubo"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="lunghtubo"
                label="Lunghezza tubo"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="sptemp"
                label="Temperatura"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="viscosp"
                label="Viscosità"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="row" gap={0.5}>
                <Typography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  flex={1}
                >
                  Viscosità reale
                </Typography>
                <Typography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  flex={1}
                  textAlign="end"
                >
                  {viscositaReale}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="nreynolds"
                label="Numero di Reynolds"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="ncurve"
                label="N curve"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberRegistroCell
                control={control}
                fieldName="coeffcurvk"
                label="Coefficiente curva K"
                readOnly={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  mt: 1,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle1" fontWeight="bold">
                        Risultato:
                      </Typography>
                      <Typography variant="h6">
                        {portPidValue !== null ? `${portPidValue} L/MIN` : "-"}
                      </Typography>
                    </Stack>
                  </Grid>
                  {!portataInRange && portPidValue !== null && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        Calcolo portata fuori range (10-1000 L/MIN)
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      onClick={handleCalcola}
                      disabled={controlsLocked}
                      startIcon={
                        calcolaPortataLoading && (
                          <SmallLoading sx={{ color: "inherit" }} />
                        )
                      }
                      fullWidth
                    >
                      Calcola
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="SP ricircolo">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="tempricir"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="velpricirc"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="SP allarmi generale">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allDP2-4"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allDP3-4"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="SP allarmi flussaggio">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allPminF"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allPmaxF"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allPminTF"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allaltaT"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="SP allarmi flussaggio">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="spcont4"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="spcont6"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="spcont14"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="spcont21"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionCard title="SP allarmi ricircolo">
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allTempminricir"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allTempmaxricir"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <NumberRegistroCell
                control={control}
                fieldName="allTmaxricir"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Email">
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email1"
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextRegistroCell
                control={control}
                fieldName="email2"
                readOnly={readOnly}
              />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid item xs={12}>
          <SectionCard title="Altro">
            {otherFields.map((fieldName) => {
              if (numericFieldMap[fieldName]) {
                return (
                  <Grid item xs={12} md={6} key={fieldName}>
                    <NumberRegistroCell
                      control={control}
                      fieldName={fieldName}
                      readOnly={readOnly}
                    />
                  </Grid>
                );
              }

              if (boolFieldMap[fieldName]) {
                return (
                  <Grid item xs={12} md={6} key={fieldName}>
                    <BooleanField
                      control={control}
                      config={boolFieldMap[fieldName]}
                      readOnly={readOnly}
                    />
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} md={6} key={fieldName}>
                  <TextRegistroCell
                    control={control}
                    fieldName={fieldName}
                    readOnly={readOnly}
                  />
                </Grid>
              );
            })}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}

function NumberField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_BANCO_DI_FLUSSAGGIO>;
  config: NumberFieldConfig;
  readOnly: boolean;
}) {
  const decimals = config.decimals ?? 0;

  return (
    <Controller
      control={control}
      name={config.name}
      rules={{
        validate: (value) => {
          if (isNil(value) || value === "") {
            return true;
          }

          const numeric = Number(value);
          if (!Number.isFinite(numeric)) {
            return "Inserisci un numero valido";
          }

          if (!isNil(config.min) && numeric < config.min) {
            return `Il valore deve essere maggiore o uguale a ${config.min}`;
          }

          if (!isNil(config.max) && numeric > config.max) {
            return `Il valore deve essere minore o uguale a ${config.max}`;
          }

          return true;
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <Stack
          direction="column"
          alignItems="end"
          sx={{ width: "100%", maxWidth: 200 }}
        >
          <NumberInput
            {...field}
            numericInputProps={{ decimalScale: decimals }}
            endAdornment={
              config.unit ? (
                <InputAdornment position="end">{config.unit}</InputAdornment>
              ) : undefined
            }
            disabled={readOnly}
          />
          {error && <FormHelperText error>{error.message}</FormHelperText>}
        </Stack>
      )}
    />
  );
}

function BooleanField({
  control,
  config,
  readOnly,
}: {
  control: Control<Registri_BANCO_DI_FLUSSAGGIO>;
  config: BoolFieldConfig;
  readOnly: boolean;
}) {
  return (
    <Controller
      name={config.name}
      control={control}
      render={({ field }) => (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="button"
            fontWeight="regular"
            color="text"
            sx={{ minWidth: 220 }}
          >
            {config.label}
          </Typography>
          <Switch
            checked={field.value || false}
            onChange={(event) => field.onChange(event.target.checked)}
            disabled={readOnly}
          />
        </Stack>
      )}
    />
  );
}

function NumberRegistroCell({
  control,
  fieldName,
  label,
  readOnly,
}: {
  control: Control<Registri_BANCO_DI_FLUSSAGGIO>;
  fieldName: RegistroFieldName;
  label?: string;
  readOnly: boolean;
}) {
  const baseConfig = numericFieldMap[fieldName];
  if (!baseConfig) {
    return null;
  }

  const config = label ? { ...baseConfig, label } : baseConfig;

  return (
    <Box display="flex" flexDirection="row" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text" flex={1}>
        {config.label}
      </Typography>
      <NumberField control={control} config={config} readOnly={readOnly} />
    </Box>
  );
}

function TextRegistroCell({
  control,
  fieldName,
  readOnly,
}: {
  control: Control<Registri_BANCO_DI_FLUSSAGGIO>;
  fieldName: RegistroFieldName;
  readOnly: boolean;
}) {
  const config = textFieldMap[fieldName];
  if (!config) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="button" fontWeight="regular" color="text">
        {config.label}
      </Typography>
      <Controller
        control={control}
        name={config.name}
        render={({ field, fieldState: { error } }) => (
          <Stack direction="column" alignItems="end" sx={{ width: "100%" }}>
            <TextField
              {...field}
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!error}
            />
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </Stack>
        )}
      />
    </Box>
  );
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <Box p={2}>
        <Typography variant="h5" color="text" fontWeight="medium" gutterBottom>
          {title}
        </Typography>
        <Grid container spacing={2} mt={1}>
          {children}
        </Grid>
      </Box>
    </Card>
  );
}
